import React, { useState, useEffect, useCallback } from 'react';
import { feedAPI } from '../../api';
import { 
  Heart, MessageCircle, Share2, MoreHorizontal, Send, 
  Trophy, Dumbbell, Award, Target, Image, X, Trash2,
  ChevronDown
} from 'lucide-react';

const POST_TYPE_ICONS = {
  workout_completed: Dumbbell,
  pr_achieved: Trophy,
  badge_earned: Award,
  challenge_completed: Target,
  milestone: Trophy,
  text: MessageCircle,
  photo: Image,
};

const POST_TYPE_COLORS = {
  workout_completed: 'bg-indigo-100 text-indigo-600',
  pr_achieved: 'bg-yellow-100 text-yellow-600',
  badge_earned: 'bg-purple-100 text-purple-600',
  challenge_completed: 'bg-green-100 text-green-600',
  milestone: 'bg-pink-100 text-pink-600',
  text: 'bg-gray-100 text-gray-600',
  photo: 'bg-blue-100 text-blue-600',
};

export function SocialFeed({ currentUser }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async (loadMore = false) => {
    try {
      const params = { limit: 20 };
      if (loadMore && cursor) params.cursor = cursor;
      
      const response = await feedAPI.getFeed(params);
      
      if (loadMore) {
        setPosts(prev => [...prev, ...response.data.posts]);
      } else {
        setPosts(response.data.posts);
      }
      setHasMore(response.data.has_more);
      setCursor(response.data.next_cursor);
    } catch (error) {
      console.error('Error fetching feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId, isLiked) => {
    try {
      if (isLiked) {
        await feedAPI.unlikePost(postId);
      } else {
        await feedAPI.likePost(postId);
      }
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            is_liked_by_user: !isLiked,
            likes_count: isLiked ? post.likes_count - 1 : post.likes_count + 1
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await feedAPI.deletePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
    setShowPostForm(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-feed">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Community Feed</h1>
          <p className="text-gray-600">See what others are achieving</p>
        </div>
      </div>

      {/* Create Post */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div
          onClick={() => setShowPostForm(true)}
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <span className="text-indigo-600 font-semibold">
              {currentUser?.name?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-gray-500 text-sm">
            Share your progress...
          </div>
        </div>
      </div>

      {/* Posts */}
      {posts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No posts yet</h3>
          <p className="text-gray-500 mt-1">Be the first to share something!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              currentUserId={currentUser?.id}
              onLike={() => handleLike(post.id, post.is_liked_by_user)}
              onDelete={() => handleDeletePost(post.id)}
            />
          ))}
          
          {hasMore && (
            <button
              onClick={() => fetchPosts(true)}
              className="w-full py-3 text-indigo-600 hover:bg-indigo-50 rounded-xl transition flex items-center justify-center gap-2"
            >
              <ChevronDown className="w-5 h-5" />
              Load More
            </button>
          )}
        </div>
      )}

      {/* Create Post Modal */}
      {showPostForm && (
        <CreatePostModal
          currentUser={currentUser}
          onClose={() => setShowPostForm(false)}
          onPostCreated={handlePostCreated}
        />
      )}
    </div>
  );
}

function PostCard({ post, currentUserId, onLike, onDelete }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  const Icon = POST_TYPE_ICONS[post.post_type] || MessageCircle;
  const colorClass = POST_TYPE_COLORS[post.post_type] || 'bg-gray-100 text-gray-600';

  const fetchComments = async () => {
    if (comments.length > 0) return;
    setLoadingComments(true);
    try {
      const response = await feedAPI.getComments(post.id);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleShowComments = () => {
    setShowComments(!showComments);
    if (!showComments) fetchComments();
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      const response = await feedAPI.addComment(post.id, { content: newComment });
      setComments(prev => [...prev, response.data]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await feedAPI.deleteComment(post.id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`post-${post.id}`}>
      {/* Post Header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold">
                {post.user_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{post.user_name}</p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{formatTime(post.created_at)}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${colorClass}`}>
                  <Icon className="w-3 h-3 inline mr-1" />
                  {post.post_type.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
          {post.user_id === currentUserId && (
            <button
              onClick={onDelete}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Post Content */}
        <p className="mt-3 text-gray-800">{post.content}</p>

        {/* Related Data */}
        {post.related_data && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            {post.post_type === 'workout_completed' && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">
                  <strong>{post.related_data.duration_minutes}</strong> min
                </span>
                <span className="text-gray-600">
                  <strong>{post.related_data.total_sets}</strong> sets
                </span>
                <span className="text-gray-600">
                  <strong>{post.related_data.total_volume?.toLocaleString()}</strong> kg volume
                </span>
                {post.related_data.prs_achieved > 0 && (
                  <span className="text-yellow-600 flex items-center gap-1">
                    <Trophy className="w-4 h-4" />
                    {post.related_data.prs_achieved} PR{post.related_data.prs_achieved > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            {post.post_type === 'pr_achieved' && (
              <div className="text-sm">
                <p className="text-gray-600">
                  <strong>{post.related_data.exercise_name}</strong>: {post.related_data.value} {post.related_data.unit}
                </p>
                {post.related_data.improvement && (
                  <p className="text-green-600">+{post.related_data.improvement}% improvement</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Post Image */}
        {post.image_url && (
          <div className="mt-3">
            <img
              src={post.image_url}
              alt="Post"
              className="w-full rounded-lg max-h-96 object-cover"
            />
          </div>
        )}
      </div>

      {/* Post Actions */}
      <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-6">
        <button
          onClick={onLike}
          className={`flex items-center gap-2 text-sm ${
            post.is_liked_by_user ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
          }`}
          data-testid={`like-btn-${post.id}`}
        >
          <Heart className={`w-5 h-5 ${post.is_liked_by_user ? 'fill-current' : ''}`} />
          {post.likes_count}
        </button>
        <button
          onClick={handleShowComments}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-500"
          data-testid={`comment-btn-${post.id}`}
        >
          <MessageCircle className="w-5 h-5" />
          {post.comments_count}
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {loadingComments ? (
            <div className="py-4 text-center text-gray-500">Loading comments...</div>
          ) : (
            <>
              <div className="space-y-3 py-3">
                {comments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center">No comments yet</p>
                ) : (
                  comments.map(comment => (
                    <div key={comment.id} className="flex gap-2">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-600 text-xs font-semibold">
                          {comment.user_name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex justify-between items-start">
                          <p className="text-sm font-medium text-gray-900">{comment.user_name}</p>
                          {comment.user_id === currentUserId && (
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-gray-700">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Add Comment */}
              <form onSubmit={handleAddComment} className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-full text-sm focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim()}
                  className="p-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePostModal({ currentUser, onClose, onPostCreated }) {
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [postType, setPostType] = useState('text');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      setError('Please enter some content');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await feedAPI.createPost({
        content,
        post_type: postType,
        image_url: imageUrl || null,
        is_public: true
      });
      onPostCreated(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-post-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-4 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Create Post</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold">
                {currentUser?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{currentUser?.name}</p>
              <select
                value={postType}
                onChange={(e) => setPostType(e.target.value)}
                className="text-xs text-gray-500 border-none p-0 focus:ring-0"
              >
                <option value="text">Text Post</option>
                <option value="photo">Photo</option>
                <option value="milestone">Milestone</option>
              </select>
            </div>
          </div>

          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?"
            rows={4}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
            data-testid="post-content-input"
          />

          {postType === 'photo' && (
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Image URL"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
            />
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !content.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              data-testid="submit-post-btn"
            >
              {saving ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SocialFeed;
